import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPieChart, FiTrendingUp, FiClock, FiDollarSign, FiActivity, FiTarget, FiAlertCircle, FiCheckCircle } = FiIcons;

const PortfolioSummary = ({ goals, userProfile }) => {
  // 1. Calculate Goal Counts
  const totalGoals = goals.length;
  // Short Term: 0-3 Years
  const shortTermGoals = goals.filter(g => parseInt(g.yearsFromNow) <= 3).length;
  // Medium Term: 3-9 Years (Updated from 3-7)
  const mediumTermGoals = goals.filter(g => parseInt(g.yearsFromNow) > 3 && parseInt(g.yearsFromNow) <= 9).length;
  // Long Term: 10+ Years (Updated from 7+)
  const longTermGoals = goals.filter(g => parseInt(g.yearsFromNow) >= 10).length;

  // 2. Calculate Financial Totals
  const totalCurrentCost = goals.reduce((sum, goal) => sum + parseInt(goal.currentCost), 0);
  const totalCurrentSIP = goals.reduce((sum, goal) => {
    return sum + (goal.currentlyInvesting ? parseInt(goal.currentSIP || 0) : 0);
  }, 0);

  // 3. Advanced Calculations (Future Value, Required SIP, Lumpsum)
  let totalFutureCost = 0;
  let totalRequiredSIP = 0;
  let totalRequiredLumpsum = 0;

  goals.forEach(goal => {
    const years = parseInt(goal.yearsFromNow);
    const currentCost = parseInt(goal.currentCost);
    
    // Determine inflation rate based on goal type
    let inflationRate = 6.5;
    const lowerName = goal.name.toLowerCase();
    if (lowerName.includes('education')) inflationRate = 10.5;
    else if (lowerName.includes('health') || lowerName.includes('medical')) inflationRate = 9.5;

    // Future Value
    const futureValue = currentCost * Math.pow(1 + inflationRate / 100, years);
    totalFutureCost += futureValue;

    // Expected Return (User input or Default fallback)
    // Updated defaults to align loosely with new buckets if needed, but keeping logic consistent
    const expectedReturn = goal.expectedReturn 
      ? parseFloat(goal.expectedReturn) 
      : (years >= 10 ? 12 : years > 3 ? 10 : 7);

    // SIP Calculation
    const monthlyReturn = expectedReturn / 100 / 12;
    const months = years * 12;
    const sip = (monthlyReturn === 0) 
      ? futureValue / months 
      : (futureValue * monthlyReturn) / (Math.pow(1 + monthlyReturn, months) - 1);

    // Lumpsum Calculation
    const annualRate = expectedReturn / 100;
    const lumpsum = futureValue / Math.pow(1 + annualRate, years);

    totalRequiredSIP += sip;
    totalRequiredLumpsum += lumpsum;
  });

  const sipGap = totalRequiredSIP - totalCurrentSIP;
  const isGapPositive = sipGap > 0;
  const coveragePercent = totalRequiredSIP > 0 ? Math.min((totalCurrentSIP / totalRequiredSIP) * 100, 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8"
    >
      {/* Header */}
      <div className="bg-white p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-100 p-2 rounded-lg">
              <SafeIcon icon={FiPieChart} className="text-2xl text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Portfolio Executive Summary</h2>
              <p className="text-sm text-gray-500">Overview of your financial requirements</p>
            </div>
          </div>
          <div className="hidden md:block bg-indigo-50 px-4 py-2 rounded-full text-xs font-semibold text-indigo-700">
            {totalGoals} Active Goal{totalGoals !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Top Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Card 1: Future Wealth Needed */}
          <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl p-5 text-white shadow-lg relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <SafeIcon icon={FiTarget} className="text-6xl" />
            </div>
            <div className="relative z-10">
              <p className="text-indigo-100 text-sm font-medium mb-1">Target Wealth Corpus</p>
              <h3 className="text-2xl font-bold mb-2">₹{Math.round(totalFutureCost / 100000).toLocaleString('en-IN')} Lakhs</h3>
              <div className="flex items-center text-xs text-indigo-200 bg-black/20 rounded-lg px-2 py-1 w-fit">
                <SafeIcon icon={FiDollarSign} className="mr-1" />
                Current Cost: ₹{Math.round(totalCurrentCost / 100000).toLocaleString('en-IN')} L
              </div>
            </div>
          </div>

          {/* Card 2: Monthly Investment Status */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm relative">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-gray-500 text-sm font-medium">Required Monthly SIP</p>
                <h3 className="text-2xl font-bold text-gray-900">₹{Math.round(totalRequiredSIP).toLocaleString('en-IN')}</h3>
              </div>
              <div className={`p-2 rounded-full ${isGapPositive ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                <SafeIcon icon={FiActivity} className="text-xl" />
              </div>
            </div>
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-gray-600">Coverage</span>
                <span className={`font-bold ${isGapPositive ? 'text-red-600' : 'text-green-600'}`}>
                  {Math.round(coveragePercent)}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${isGapPositive ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${coveragePercent}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Current SIP: <span className="font-semibold text-gray-700">₹{totalCurrentSIP.toLocaleString('en-IN')}</span>
              </p>
            </div>
          </div>

          {/* Card 3: Investment Gap / Surplus */}
          <div className={`border rounded-xl p-5 shadow-sm flex flex-col justify-center ${isGapPositive ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={isGapPositive ? FiAlertCircle : FiCheckCircle} className={`text-xl ${isGapPositive ? 'text-red-600' : 'text-green-600'}`} />
              <span className={`font-bold ${isGapPositive ? 'text-red-800' : 'text-green-800'}`}>
                {isGapPositive ? 'Investment Gap' : 'Fully Funded'}
              </span>
            </div>
            {isGapPositive ? (
              <>
                <p className="text-sm text-red-600 mb-1">You need to increase your SIP by</p>
                <h3 className="text-3xl font-bold text-red-700">₹{Math.round(sipGap).toLocaleString('en-IN')}</h3>
                <p className="text-xs text-red-500 mt-1">per month to achieve all goals</p>
              </>
            ) : (
              <>
                <p className="text-sm text-green-600 mb-1">Surplus Investment</p>
                <h3 className="text-3xl font-bold text-green-700">₹{Math.abs(Math.round(sipGap)).toLocaleString('en-IN')}</h3>
                <p className="text-xs text-green-600 mt-1">You are investing more than required!</p>
              </>
            )}
          </div>
        </div>

        {/* Detailed Breakdown Grid */}
        <div className="grid md:grid-cols-2 gap-8 border-t border-gray-100 pt-8">
          {/* Timeline Distribution */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiClock} className="mr-2 text-indigo-600" />
              Goal Timeline Distribution
            </h4>
            <div className="space-y-4">
              {/* Short Term */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                    <span className="font-medium text-gray-700">Short Term (0-3 Yrs)</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-4 mt-1">Rec: Liquid / Debt Funds</p>
                </div>
                <div className="bg-white px-3 py-1 rounded border border-gray-200 font-bold text-gray-800">
                  {shortTermGoals}
                </div>
              </div>

              {/* Medium Term */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                    <span className="font-medium text-gray-700">Medium Term (3-9 Yrs)</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-4 mt-1">Rec: Hybrid / Balanced Funds</p>
                </div>
                <div className="bg-white px-3 py-1 rounded border border-gray-200 font-bold text-gray-800">
                  {mediumTermGoals}
                </div>
              </div>

              {/* Long Term */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="font-medium text-gray-700">Long Term (10+ Yrs)</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-4 mt-1">Rec: Equity Mutual Funds</p>
                </div>
                <div className="bg-white px-3 py-1 rounded border border-gray-200 font-bold text-gray-800">
                  {longTermGoals}
                </div>
              </div>
            </div>
          </div>

          {/* Lumpsum Alternative */}
          <div className="flex flex-col">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="mr-2 text-indigo-600" />
              Alternative Strategy
            </h4>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100 flex-1 flex flex-col justify-center">
              <p className="text-indigo-800 font-medium mb-2">Lumpsum Investment Option</p>
              <p className="text-sm text-indigo-600 mb-4">
                Instead of a monthly SIP, you can achieve all your goals by investing a one-time amount today in the appropriate goal-aligned funds.
              </p>
              <div className="mt-auto">
                <div className="text-3xl font-bold text-indigo-700">
                  ₹{Math.round(totalRequiredLumpsum).toLocaleString('en-IN')}
                </div>
                <p className="text-xs text-indigo-500 mt-1">Required one-time investment today</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioSummary;